import React, { useState, useEffect } from 'react';
import { Shield, Plus, Search, Edit, Eye, CheckCircle, XCircle, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, Home, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { useNotification } from '../../hooks/useNotification';
import { PerfilAcesso } from '../../types/Permission';
import { Link } from 'react-router-dom';
import PerfilAcessoModal from '../../components/PerfilAcessoModal';

const PerfisAcesso: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, NotificationComponent } = useNotification();
  const [perfis, setPerfis] = useState<PerfilAcesso[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'nome' | 'cargoBase' | 'status'>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedPerfil, setSelectedPerfil] = useState<PerfilAcesso | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showPerfilModal, setShowPerfilModal] = useState(false);

  // Carregar perfis do Firebase
  useEffect(() => {
    const loadPerfis = async () => {
      if (!userProfile?.empresaCnpj) return;

      try {
        setLoading(true);
        setError(null);
        const data = await firebaseService.getPerfisAcesso(userProfile.empresaCnpj);
        setPerfis(data);
        console.log('✅ Perfis de acesso carregados:', data.length);
      } catch (err) {
        console.error('❌ Erro ao carregar perfis de acesso:', err);
        setError('Erro ao carregar perfis de acesso');
      } finally {
        setLoading(false);
      }
    };

    loadPerfis();
  }, [userProfile?.empresaCnpj]);

  // Função de ordenação
  const handleSort = (field: 'nome' | 'cargoBase' | 'status') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Filtrar e ordenar perfis
  const filteredAndSortedPerfis = perfis
    .filter(perfil => {
      const matchesSearch = (perfil.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (perfil.descricao || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (perfil.cargoBase || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'todos' || 
                           (statusFilter === 'ativo' && perfil.ativo) ||
                           (statusFilter === 'inativo' && !perfil.ativo);
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: string | boolean;
      let bValue: string | boolean;

      switch (sortBy) {
        case 'nome':
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
          break;
        case 'cargoBase':
          aValue = (a.cargoBase || '').toLowerCase();
          bValue = (b.cargoBase || '').toLowerCase();
          break;
        case 'status':
          aValue = a.ativo;
          bValue = b.ativo;
          break;
        default:
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  // Estatísticas
  const totalPerfis = perfis.length;
  const perfisAtivos = perfis.filter(p => p.ativo).length;
  const perfisInativos = totalPerfis - perfisAtivos;

  // Funções de modal
  const handleViewPerfil = (perfil: PerfilAcesso) => {
    setSelectedPerfil(perfil);
    setShowModal(true);
  };

  const handleEditPerfil = (perfil: PerfilAcesso) => {
    setSelectedPerfil(perfil);
    setShowPerfilModal(true);
  };

  const handleNewPerfil = () => {
    setSelectedPerfil(null);
    setShowPerfilModal(true);
  };

  const handleClosePerfilModal = () => {
    setShowPerfilModal(false);
    setSelectedPerfil(null);
  };

  const handleSavePerfil = async (perfilData: Partial<PerfilAcesso>) => {
    if (!userProfile?.empresaCnpj) return;

    try {
      if (perfilData.id) {
        // Atualizar perfil existente
        await firebaseService.updatePerfilAcesso(userProfile.empresaCnpj, perfilData.id, perfilData);
      } else {
        // Criar novo perfil
        await firebaseService.createPerfilAcesso(userProfile.empresaCnpj, perfilData);
      }
      
      // Recarregar dados
      const data = await firebaseService.getPerfisAcesso(userProfile.empresaCnpj);
      setPerfis(data);
      
      handleClosePerfilModal();
      showSuccess(
        perfilData.id ? 'Perfil atualizado' : 'Perfil criado',
        perfilData.id ? 'Os dados do perfil foram atualizados com sucesso!' : 'O perfil foi criado com sucesso!'
      );
    } catch (err: any) {
      console.error('❌ Erro ao salvar perfil:', err);
      showError('Erro ao salvar', err.message || 'Não foi possível salvar o perfil. Tente novamente.');
    }
  };

  const handleDeletePerfil = async (perfil: PerfilAcesso) => {
    if (!userProfile?.empresaCnpj) return;

    showConfirm(
      'Excluir Perfil de Acesso',
      `Tem certeza que deseja excluir o perfil "${perfil.nome}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          await firebaseService.deletePerfilAcesso(userProfile.empresaCnpj, perfil.id);
          
          // Recarregar dados
          const data = await firebaseService.getPerfisAcesso(userProfile.empresaCnpj);
          setPerfis(data);
          
          showSuccess('Perfil excluído', 'O perfil foi excluído com sucesso!');
        } catch (err: any) {
          console.error('❌ Erro ao excluir perfil:', err);
          showError('Erro ao excluir', err.message || 'Não foi possível excluir o perfil. Tente novamente.');
        }
      }
    );
  };

  const getSortIcon = (field: 'nome' | 'cargoBase' | 'status') => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary-600" />
      : <ArrowDown className="h-4 w-4 text-primary-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando perfis de acesso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NotificationComponent />

      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/" className="hover:text-gray-700">
          <Home className="h-4 w-4" />
        </Link>
        <span>/</span>
        <Link to="/configuracoes" className="hover:text-gray-700">Configurações</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Perfis de Acesso</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Perfis de Acesso</h1>
          <p className="text-gray-600 mt-1">Gerencie permissões e níveis de acesso por cargo</p>
        </div>
        <button
          onClick={handleNewPerfil}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Novo Perfil
        </button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Perfis</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalPerfis}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <Shield className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Perfis Ativos</p>
              <p className="text-2xl font-bold text-success-600 mt-1">{perfisAtivos}</p>
            </div>
            <div className="p-3 bg-success-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-success-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Perfis Inativos</p>
              <p className="text-2xl font-bold text-gray-500 mt-1">{perfisInativos}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-lg">
              <XCircle className="h-6 w-6 text-gray-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, descrição ou cargo base..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="todos">Todos os status</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('nome')}
                >
                  <div className="flex items-center gap-2">
                    Nome
                    {getSortIcon('nome')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('cargoBase')}
                >
                  <div className="flex items-center gap-2">
                    Cargo Base
                    {getSortIcon('cargoBase')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-2">
                    Status
                    {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedPerfis.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    {searchTerm || statusFilter !== 'todos' 
                      ? 'Nenhum perfil encontrado com os filtros aplicados'
                      : 'Nenhum perfil de acesso cadastrado'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedPerfis.map((perfil) => (
                  <tr key={perfil.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                          <Shield className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{perfil.nome}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md truncate">
                        {perfil.descricao || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {perfil.cargoBase || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {perfil.ativo ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-800 flex items-center gap-1 w-fit">
                          <CheckCircle className="h-3 w-3" />
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 flex items-center gap-1 w-fit">
                          <XCircle className="h-3 w-3" />
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewPerfil(perfil)}
                          className="text-primary-600 hover:text-primary-900 p-1"
                          title="Visualizar"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEditPerfil(perfil)}
                          className="text-gray-600 hover:text-gray-900 p-1"
                          title="Editar"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeletePerfil(perfil)}
                          className="text-danger-600 hover:text-danger-900 p-1"
                          title="Excluir"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Visualização */}
      {showModal && selectedPerfil && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Detalhes do Perfil de Acesso</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                  <Shield className="h-8 w-8 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedPerfil.nome}</h3>
                  <p className="text-sm text-gray-600">{selectedPerfil.descricao || 'Sem descrição'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-sm font-medium text-gray-500">Cargo Base</p>
                  <p className="text-sm text-gray-900">{selectedPerfil.cargoBase || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <p className="text-sm text-gray-900">
                    {selectedPerfil.ativo ? (
                      <span className="text-success-600 font-medium">Ativo</span>
                    ) : (
                      <span className="text-gray-500 font-medium">Inativo</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Menus Configurados</p>
                  <p className="text-sm text-gray-900">
                    {Object.keys(selectedPerfil.permissoes.menus || {}).length} menu(s)
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Módulos Configurados</p>
                  <p className="text-sm text-gray-900">
                    {Object.keys(selectedPerfil.permissoes.modulos || {}).length} módulo(s)
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowModal(false);
                  handleEditPerfil(selectedPerfil);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
              >
                Editar
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criar/Editar */}
      {showPerfilModal && (
        <PerfilAcessoModal
          perfil={selectedPerfil}
          onClose={handleClosePerfilModal}
          onSave={handleSavePerfil}
        />
      )}
    </div>
  );
};

export default PerfisAcesso;

